import React, { useMemo } from 'react'
import { ObjectType, ShareRecipientResponse } from '@/modules/Drive/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { t } from 'i18next'
import FolderIcons from '@/icons/FolderIcons/FolderIcons'
import { extractExtension, FileIcon } from '@/modules/Drive/utils'
import ImageIcons from '@/icons/ImageIcons'
import { ObjectInfo } from '.'
import { Avatar } from 'vnetwork-ui'
import { randomAvatarSrc } from '@/utils/function'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import { Globe, Users } from 'lucide-react'
import { Loading } from 'vnetwork-ui'

interface ObjectInfoFormProps {
    onClose: () => void
    objectName: string
    objectKey: string
    objectType: ObjectType
    size?: number
    lastModified?: string
    objectInfo?: ObjectInfo
    sharedRecipientInfo?: ShareRecipientResponse
    isLoading?: boolean
    error?: any
}

const ObjectInfoForm: React.FC<ObjectInfoFormProps> = ({
    onClose,
    objectName,
    objectKey,
    objectType,
    size,
    lastModified,
    objectInfo,
    sharedRecipientInfo,
    isLoading,
    error,
}) => {
    // Responsive maximum number of recipient avatars to display
    const [maxVisibleRecipients, setMaxVisibleRecipients] = React.useState(5)

    // Update max visible based on screen width
    React.useEffect(() => {
        const updateMaxVisible = () => {
            const width = window.innerWidth
            if (width >= 1280) {
                // xl screens and above
                setMaxVisibleRecipients(10)
            } else if (width >= 1024) {
                // lg screens
                setMaxVisibleRecipients(8)
            } else if (width >= 768) {
                // md screens
                setMaxVisibleRecipients(6)
            } else {
                // sm screens
                setMaxVisibleRecipients(5)
            }
        }

        updateMaxVisible()
        window.addEventListener('resize', updateMaxVisible)
        return () => window.removeEventListener('resize', updateMaxVisible)
    }, [])

    // Sort recipients: departments first, then employees
    const sortedRecipients = React.useMemo(() => {
        if (!sharedRecipientInfo?.recipient_list) return []
        return [...sharedRecipientInfo.recipient_list].sort((a, b) => {
            // Departments come first
            if (
                a.share_recipient_type === 'department' &&
                b.share_recipient_type !== 'department'
            )
                return -1
            if (
                b.share_recipient_type === 'department' &&
                a.share_recipient_type !== 'department'
            )
                return 1
            return 0
        })
    }, [sharedRecipientInfo?.recipient_list])

    // Calculate how many recipients are hidden
    const totalRecipients = sortedRecipients.length
    const visibleRecipients = sortedRecipients.slice(0, maxVisibleRecipients)
    const hiddenRecipientsCount = Math.max(
        0,
        totalRecipients - maxVisibleRecipients
    )

    const formatFileSize = (bytes?: number): string => {
        if (!bytes) return 'N/A'
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
        if (bytes === 0) return '0 Bytes'
        const i = Math.floor(Math.log(bytes) / Math.log(1024))
        return (
            Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i]
        )
    }

    const formatDate = (dateString?: string): string => {
        if (!dateString) return 'N/A'
        try {
            const date = new Date(dateString)
            return date.toLocaleDateString('vi-VN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            })
        } catch {
            return dateString
        }
    }

    const getFileTypeDisplay = (): string => {
        if (objectType === ObjectType.FOLDER) {
            return t('drive.folder')
        }
        const extension = extractExtension(objectKey)
        return extension ? extension.toUpperCase() + ' File' : 'File'
    }

    const iconNode = useMemo(
        () =>
            objectType === ObjectType.FOLDER ? (
                <FolderIcons />
            ) : FileIcon[extractExtension(objectKey).toLowerCase()] ? (
                FileIcon[extractExtension(objectKey).toLowerCase()]
            ) : (
                <ImageIcons className="text-red-500" />
            ),
        [objectType, objectKey]
    )

    // Show loading state
    if (isLoading) {
        return (
            <div className="w-full">
                <Card className="border-0 shadow-none">
                    <CardContent className="flex items-center justify-center py-12">
                        <Loading.CircularProgress width={20} height={20} />
                    </CardContent>
                </Card>
            </div>
        )
    }

    // Show error state
    if (error) {
        return (
            <div className="w-full">
                <Card className="border-0 shadow-none">
                    <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
                        <div className="text-red-500 text-center">
                            <div className="text-lg font-semibold">
                                {t('error.loading-failed')}
                            </div>
                            <div className="text-sm text-gray-500 mt-2">
                                {error?.message || t('error.generic')}
                            </div>
                        </div>
                        <Button variant="outline" onClick={onClose}>
                            {t('close')}
                        </Button>
                    </CardContent>
                </Card>
            </div>
        )
    }

    return (
        <div className="w-full">
            <Card className="border-0 shadow-none">
                <CardHeader className="pb-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <div className="size-10">{iconNode}</div>
                        <div className="flex-1 min-w-0">
                            <CardTitle className="text-lg font-semibold break-words">
                                {objectName}
                            </CardTitle>
                        </div>
                    </div>
                </CardHeader>

                <CardContent className="space-y-4">
                    {/* Owner Information */}
                    <div className="space-y-3">
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900">
                                {t('drive.owner')}
                            </div>
                        </div>
                        <div className="flex justify-between">
                            <div className="text-sm font-medium text-gray-600 break-words">
                                {objectInfo?.owner_info.employee_name}
                            </div>
                            <div className="text-xs text-gray-500 break-all">
                                {objectInfo?.owner_info.employee_email_company}
                            </div>
                        </div>
                    </div>

                    <Separator />

                    {/* File Details */}
                    <div className="space-y-3">
                        {/* Size */}
                        {objectType === ObjectType.FILE && (
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700">
                                    {t('drive.size')}
                                </span>
                                <span className="text-sm text-gray-600">
                                    {formatFileSize(size)}
                                </span>
                            </div>
                        )}

                        {/* Type */}
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">
                                {t('drive.type')}
                            </span>
                            <span className="text-sm text-gray-600">
                                {getFileTypeDisplay()}
                            </span>
                        </div>
                    </div>

                    <Separator />

                    {/* Timestamps */}
                    {lastModified && (
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900">
                                        {t('drive.timestamps')}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-between items-start">
                                <span className="text-sm text-gray-700">
                                    {t('drive.last-modified')}
                                </span>
                                <span className="text-sm text-gray-600 text-right">
                                    {formatDate(lastModified)}
                                </span>
                            </div>
                        </div>
                    )}

                    {lastModified && <Separator />}

                    {/* Sharing Information */}
                    <div className="space-y-3">
                        <div className="text-sm font-medium text-gray-900">
                            {t('existing-access')}
                        </div>
                        <div className="flex flex-wrap gap-2 items-center">
                            {/* Owner Avatar */}
                            {sharedRecipientInfo?.owner && (
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <div className="cursor-pointer relative">
                                            <div className="border-2 border-white shadow-sm hover:shadow-md transition-shadow rounded-full overflow-hidden">
                                                <Avatar
                                                    src={randomAvatarSrc(
                                                        sharedRecipientInfo
                                                            .owner.owner_email
                                                    )}
                                                    size="medium"
                                                />
                                            </div>
                                        </div>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        className="w-64 z-[99999]"
                                        align="start"
                                    >
                                        <div className="space-y-2">
                                            <div className="text-sm font-semibold">
                                                {
                                                    sharedRecipientInfo.owner
                                                        .owner_name
                                                }
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {
                                                    sharedRecipientInfo.owner
                                                        .owner_email
                                                }
                                            </div>
                                            <Badge
                                                variant="default"
                                                className="text-xs"
                                            >
                                                {t('owner')}
                                            </Badge>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            )}

                            {/* Vertical Separator after Owner */}
                            {sharedRecipientInfo?.owner &&
                                (sharedRecipientInfo.recipient_list.length >
                                    0 ||
                                    objectInfo?.has_public_access) && (
                                    <div className="h-10 w-px bg-gray-300" />
                                )}

                            {/* Public Access Avatar */}
                            {objectInfo?.has_public_access && (
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <div className="cursor-pointer">
                                            <div className="size-10 bg-blue-100 text-blue-600 flex items-center justify-center rounded-full border-2 border-white shadow-sm hover:shadow-md transition-shadow">
                                                <Globe className="w-5 h-5" />
                                            </div>
                                        </div>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        className="w-64 z-[99999]"
                                        align="start"
                                    >
                                        <div className="space-y-2">
                                            <div className="text-sm font-semibold">
                                                {t('drive.public-access')}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {t('drive.anyone-with-link')}
                                            </div>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            )}

                            {/* Recipient Avatars */}
                            {visibleRecipients.map((recipient) => (
                                <Popover key={recipient.share_recipient_id}>
                                    <PopoverTrigger asChild>
                                        <div className="cursor-pointer relative">
                                            <div className="border-2 border-white shadow-sm hover:shadow-md transition-shadow rounded-full overflow-hidden">
                                                <Avatar
                                                    src={randomAvatarSrc(
                                                        recipient.recipient_email
                                                    )}
                                                    size="medium"
                                                />
                                            </div>
                                            {recipient.share_recipient_type ===
                                                'department' && (
                                                <div className="absolute -bottom-1 -right-1 bg-purple-500 text-white p-0.5 rounded-full">
                                                    <Users className="w-3 h-3" />
                                                </div>
                                            )}
                                        </div>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        className="w-64 z-[99999]"
                                        align="start"
                                    >
                                        <div className="space-y-2">
                                            <div className="text-sm font-semibold">
                                                {recipient.recipient_name}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {recipient.recipient_email}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Badge
                                                    variant="outline"
                                                    className="text-xs"
                                                >
                                                    {recipient.share_recipient_type ===
                                                    'department'
                                                        ? t('department')
                                                        : t('employee')}
                                                </Badge>
                                                <Badge
                                                    variant="secondary"
                                                    className="text-xs"
                                                >
                                                    {t(
                                                        `drive.permission.${
                                                            recipient.share_permission_level
                                                        }`
                                                    )}
                                                </Badge>
                                            </div>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            ))}

                            {/* +X more indicator */}
                            {hiddenRecipientsCount > 0 && (
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <div className="cursor-pointer">
                                            <div className="w-10 h-10 bg-gray-200 text-gray-700 flex items-center justify-center rounded-full border-2 border-white shadow-sm hover:shadow-md transition-shadow text-xs font-semibold">
                                                +{hiddenRecipientsCount}
                                            </div>
                                        </div>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        className="w-80 z-[99999] max-h-96 overflow-y-auto"
                                        align="start"
                                    >
                                        <div className="space-y-3">
                                            <div className="text-sm font-semibold">
                                                {t('all-recipients')} (
                                                {totalRecipients})
                                            </div>
                                            {sortedRecipients.map(
                                                (recipient) => (
                                                    <div
                                                        key={
                                                            recipient.share_recipient_id
                                                        }
                                                        className="flex items-center gap-3 py-2"
                                                    >
                                                        <Avatar
                                                            src={randomAvatarSrc(
                                                                recipient.recipient_email
                                                            )}
                                                            size="small"
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-xs font-medium truncate">
                                                                {
                                                                    recipient.recipient_name
                                                                }
                                                            </div>
                                                            <div className="text-xs text-gray-500 truncate">
                                                                {
                                                                    recipient.recipient_email
                                                                }
                                                            </div>
                                                        </div>
                                                        <Badge
                                                            variant="outline"
                                                            className="text-xs shrink-0"
                                                        >
                                                            {t(
                                                                `drive.permission.${recipient.share_permission_level}`
                                                            )}
                                                        </Badge>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            )}
                        </div>
                    </div>

                    {/* <Separator /> */}

                    {/* Technical Details */}
                    {/* <div className="space-y-2">
                        <div className="text-sm font-medium text-gray-900">
                            {t('drive.technical-details')}
                        </div>
                        <div className="text-xs text-gray-500 space-y-1 font-mono bg-gray-50 p-3 rounded">
                            <div className="break-all">
                                <span className="font-semibold">
                                    Object Key:
                                </span>{' '}
                                {objectKey}
                            </div>
                            <div>
                                <span className="font-semibold">
                                    Owner UUID:
                                </span>{' '}
                                {extractUuidFromObjectKey(objectKey)}
                            </div>
                        </div>
                    </div> */}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2 pt-4">
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={onClose}
                        >
                            {t('close')}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}

export default ObjectInfoForm
